/**
 * 税計算の本体と明細生成（バックエンド `TaxCalculationService` /
 * `TaxCalculationDetailService` の移植）。
 *
 * 所得税・住民税・ふるさと納税・手取り（付加税込み）を同一の `TaxRules` と
 * 家族控除で計算し、計算明細も同じロジックから生成する（計算と明細の一致）。
 */

import {
  incomeTaxFamilyDeductionBreakdown,
  humanDeductionDifference,
  incomeTaxFamilyDeductions,
  residentHousehold,
  residentTaxFamilyDeductionBreakdown,
  residentTaxFamilyDeductions,
} from './familyDeductions';
import {
  calculateFurusatoLimit,
  calculateFurusatoSafeLimit,
  calculateSpecialDeduction,
  type FurusatoSafeLimitResult,
} from './furusato';
import { calculateHousingLoan, type HousingLoanResult } from './housingLoan';
import { TaxRulesRegistry, defaultIncomeYear, eraYearLabel } from './taxRules';
import {
  createFamilyData,
  type CalculationDetail,
  type CalculationStep,
  type FamilyData,
  type TaxCalculationInput,
  type TaxCalculationWithDetails,
  type TaxRules,
} from './types';

/** 所得税の課税所得（合計所得金額 − 社会保険料 − 確定拠出年金 − 所得控除）。 */
export function calculateTaxableIncome(
  annualIncome: number,
  socialInsurance: number,
  definedContribution: number,
  familyData: FamilyData,
  rules: TaxRules,
): number {
  const salaryDeduction = rules.salaryDeduction(annualIncome);
  const totalIncome = annualIncome - salaryDeduction;
  const totalDeductions =
    rules.basicDeduction(totalIncome) + incomeTaxFamilyDeductions(familyData, totalIncome, rules);
  return annualIncome - salaryDeduction - socialInsurance - definedContribution - totalDeductions;
}

/** 所得税額（速算表・円未満切捨て）。 */
export function calculateIncomeTax(
  annualIncome: number,
  socialInsurance: number,
  definedContribution: number,
  familyData: FamilyData,
  rules: TaxRules,
): number {
  const taxableIncome = calculateTaxableIncome(annualIncome, socialInsurance, definedContribution, familyData, rules);
  if (taxableIncome <= 0) {
    return 0;
  }
  const ratePercent = Math.round(rules.incomeTaxRate(taxableIncome) * 100);
  const deduction = rules.incomeTaxDeduction(taxableIncome);
  return Math.floor((taxableIncome * ratePercent - deduction * 100) / 100);
}

/** 住民税の課税所得。 */
export function calculateResidentTaxableIncome(
  annualIncome: number,
  socialInsurance: number,
  definedContribution: number,
  familyData: FamilyData,
  rules: TaxRules,
): number {
  const salaryDeduction = rules.salaryDeduction(annualIncome);
  const totalIncome = annualIncome - salaryDeduction;
  const totalDeductions =
    rules.residentBasicDeduction(totalIncome) + residentTaxFamilyDeductions(familyData, totalIncome, rules);
  return annualIncome - salaryDeduction - socialInsurance - definedContribution - totalDeductions;
}

/** 住民税の計算過程（計算本体と明細で共有する）。 */
export interface ResidentTaxParts {
  totalIncome: number;
  taxableIncome: number;
  grossIncomeLevy: number;
  adjustmentCredit: number;
  /** 所得割（調整控除後・住宅ローン控除前）。ふるさと納税の上限算定に用いる。 */
  incomeLevy: number;
  /** 住宅借入金等特別税額控除（住民税分）として控除した額。 */
  housingLoanCredit: number;
  /** 所得割（住宅ローン控除後）。 */
  incomeLevyAfterHousingLoan: number;
  equalLevy: number;
  total: number;
  nonTaxable: boolean;
}

/**
 * 住民税の所得割・均等割・調整控除・非課税判定を計算する。
 *
 * 非課税判定は標準的な 1 級地の考え方（所得割: 35万×(本人+同一生計配偶者+扶養親族)+42万、
 * 均等割: 同+31万、扶養親族等がいない場合は 45 万円）を採用する（概算）。
 *
 * `housingLoanCredit` は住宅借入金等特別税額控除（住民税分）である。税額控除の順序
 * （調整控除 → 配当控除 → 住宅借入金等特別税額控除 → 寄附金税額控除 …）に従い、
 * 調整控除後の所得割から控除する。
 */
export function calculateResidentTaxParts(
  annualIncome: number,
  socialInsurance: number,
  definedContribution: number,
  familyData: FamilyData,
  rules: TaxRules,
  housingLoanCredit = 0,
): ResidentTaxParts {
  const totalIncome = annualIncome - rules.salaryDeduction(annualIncome);
  const household = residentHousehold(familyData, rules);
  const taxableIncome = calculateResidentTaxableIncome(
    annualIncome,
    socialInsurance,
    definedContribution,
    familyData,
    rules,
  );
  const equalLevyLimit = rules.residentEqualLevyNonTaxableLimit(household.count, household.hasDependents);
  if (totalIncome <= equalLevyLimit) {
    // 所得割・均等割とも非課税。
    return {
      totalIncome,
      taxableIncome,
      grossIncomeLevy: 0,
      adjustmentCredit: 0,
      incomeLevy: 0,
      housingLoanCredit: 0,
      incomeLevyAfterHousingLoan: 0,
      equalLevy: 0,
      total: 0,
      nonTaxable: true,
    };
  }
  const incomeLevyLimit = rules.residentIncomeLevyNonTaxableLimit(household.count, household.hasDependents);
  let grossIncomeLevy = 0;
  let adjustmentCredit = 0;
  let incomeLevy = 0;
  if (totalIncome > incomeLevyLimit && taxableIncome > 0) {
    const ratePercent = Math.round(rules.residentTaxRate() * 100);
    grossIncomeLevy = Math.floor((taxableIncome * ratePercent) / 100);
    const difference = humanDeductionDifference(familyData, totalIncome, rules);
    adjustmentCredit = rules.adjustmentCredit(taxableIncome, difference, totalIncome);
    incomeLevy = Math.max(0, grossIncomeLevy - adjustmentCredit);
  }
  const appliedHousingLoanCredit = Math.min(Math.max(0, Math.floor(housingLoanCredit)), incomeLevy);
  const incomeLevyAfterHousingLoan = incomeLevy - appliedHousingLoanCredit;
  const equalLevy = rules.residentEqualLevy();
  return {
    totalIncome,
    taxableIncome,
    grossIncomeLevy,
    adjustmentCredit,
    incomeLevy,
    housingLoanCredit: appliedHousingLoanCredit,
    incomeLevyAfterHousingLoan,
    equalLevy,
    total: incomeLevyAfterHousingLoan + equalLevy,
    nonTaxable: false,
  };
}

/** 住民税額（所得割 + 均等割。非課税判定・調整控除・住宅ローン控除を含む）。 */
export function calculateResidentTax(
  annualIncome: number,
  socialInsurance: number,
  definedContribution: number,
  familyData: FamilyData,
  rules: TaxRules,
  housingLoanCredit = 0,
): number {
  return calculateResidentTaxParts(
    annualIncome,
    socialInsurance,
    definedContribution,
    familyData,
    rules,
    housingLoanCredit,
  ).total;
}

function generateBasicDeductionDescription(rules: TaxRules, basicDeduction: number): string {
  if (basicDeduction <= 0) {
    return '基礎控除の適用なし（合計所得金額が上限を超えています）';
  }
  return `${eraYearLabel(rules.incomeYear())}分の基礎控除（合計所得金額に応じた額）`;
}

function residentBasicDeductionDescription(basicDeduction: number): string {
  if (basicDeduction <= 0) {
    return '住民税の基礎控除の適用なし（合計所得金額が上限を超えています）';
  }
  return `住民税の基礎控除（${basicDeduction / 10000}万円）`;
}

function createIncomeTaxDetail(
  annualIncome: number,
  socialInsurance: number,
  definedContribution: number,
  incomeTax: number,
  incomeTaxBeforeCredits: number,
  housingLoan: HousingLoanResult,
  familyData: FamilyData,
  rules: TaxRules,
): CalculationDetail {
  const salaryDeduction = rules.salaryDeduction(annualIncome);
  const totalIncome = annualIncome - salaryDeduction;
  const basicDeduction = rules.basicDeduction(totalIncome);
  const breakdown = incomeTaxFamilyDeductionBreakdown(familyData, totalIncome, rules);
  const classicFamily = breakdown.spouse + breakdown.dependents + breakdown.workingStudent;
  const taxableIncome = calculateTaxableIncome(annualIncome, socialInsurance, definedContribution, familyData, rules);
  const taxRate = rules.incomeTaxRate(taxableIncome);
  const incomeTaxWithSurtax = rules.incomeTaxWithSurtax(incomeTax);
  const surtax = incomeTaxWithSurtax - incomeTax;

  const steps: CalculationStep[] = [
    { label: '推定年収', value: annualIncome, formula: '給与明細から推定' },
    { label: '給与所得控除', value: salaryDeduction, formula: '年収に応じた控除額' },
    { label: '社会保険料控除', value: socialInsurance, formula: '健康保険・厚生年金・雇用保険' },
    { label: '確定拠出年金控除', value: definedContribution, formula: 'DC・iDeCo拠出額' },
    { label: '基礎控除', value: basicDeduction, formula: generateBasicDeductionDescription(rules, basicDeduction) },
    { label: '配偶者・扶養・勤労学生控除等', value: classicFamily, formula: '家族構成に応じた控除' },
  ];
  if (breakdown.specificRelatives > 0) {
    steps.push({
      label: '特定親族特別控除',
      value: breakdown.specificRelatives,
      formula: '19〜23歳の特定親族の合計所得金額に応じた控除',
    });
  }
  if (breakdown.lifeInsurance > 0) {
    steps.push({
      label: '生命保険料控除',
      value: breakdown.lifeInsurance,
      formula: '一般・介護医療・個人年金（合計上限12万円）',
    });
  }
  steps.push(
    { label: '課税所得', value: taxableIncome, formula: '年収 - 各種控除' },
    { label: '所得税率', value: Math.round(taxRate * 100), formula: '課税所得に応じた累進税率（%）' },
  );
  if (housingLoan.applicable && housingLoan.incomeTaxCredit > 0) {
    steps.push(
      { label: '所得税（税額控除前）', value: incomeTaxBeforeCredits, formula: '課税所得 × 税率' },
      {
        label: '住宅ローン控除（所得税分）',
        value: housingLoan.incomeTaxCredit,
        formula: `住宅借入金等特別控除可能額 ${housingLoan.availableCredit.toLocaleString()} 円のうち所得税から控除`,
      },
    );
  }
  steps.push(
    { label: '所得税', value: incomeTax, formula: '課税所得 × 税率 - 税額控除' },
    {
      label: '復興特別所得税等（付加税）',
      value: surtax,
      formula: `所得税 × 付加税率（${Math.round(rules.surtaxRate() * 1000) / 10}%）`,
    },
    { label: '合計', value: incomeTaxWithSurtax, formula: '所得税 + 付加税' },
  );
  return { title: '所得税の計算', steps };
}

function createResidentTaxDetail(
  annualIncome: number,
  socialInsurance: number,
  definedContribution: number,
  residentTax: number,
  housingLoan: HousingLoanResult,
  familyData: FamilyData,
  rules: TaxRules,
): CalculationDetail {
  const salaryDeduction = rules.salaryDeduction(annualIncome);
  const totalIncome = annualIncome - salaryDeduction;
  const basicDeduction = rules.residentBasicDeduction(totalIncome);
  const breakdown = residentTaxFamilyDeductionBreakdown(familyData, totalIncome, rules);
  const classicFamily = breakdown.spouse + breakdown.dependents + breakdown.workingStudent;
  const parts = calculateResidentTaxParts(
    annualIncome,
    socialInsurance,
    definedContribution,
    familyData,
    rules,
    housingLoan.residentTaxCredit,
  );

  const steps: CalculationStep[] = [
    { label: '推定年収', value: annualIncome, formula: '給与明細から推定' },
    { label: '給与所得控除', value: salaryDeduction, formula: '年収に応じた控除額' },
    { label: '社会保険料控除', value: socialInsurance, formula: '健康保険・厚生年金・雇用保険' },
    { label: '確定拠出年金控除', value: definedContribution, formula: 'DC・iDeCo拠出額' },
    { label: '基礎控除', value: basicDeduction, formula: residentBasicDeductionDescription(basicDeduction) },
    { label: '配偶者・扶養・勤労学生控除等', value: classicFamily, formula: '家族構成に応じた控除' },
  ];
  if (breakdown.lifeInsurance > 0) {
    steps.push({
      label: '生命保険料控除',
      value: breakdown.lifeInsurance,
      formula: '一般・介護医療・個人年金（合計上限7万円）',
    });
  }
  if (breakdown.specificRelatives > 0) {
    steps.push({
      label: '特定親族特別控除',
      value: breakdown.specificRelatives,
      formula: '19〜23歳の特定親族の合計所得金額に応じた控除',
    });
  }
  steps.push(
    { label: '課税所得（住民税用）', value: parts.taxableIncome, formula: '年収 - 各種控除' },
    { label: '所得割（10%）', value: parts.grossIncomeLevy, formula: '課税所得 × 10%' },
    {
      label: '調整控除',
      value: parts.adjustmentCredit,
      formula: '所得税と住民税の人的控除額の差に応じた税額控除（概算）',
    },
    { label: '所得割（調整控除後）', value: parts.incomeLevy, formula: '所得割 - 調整控除' },
  );
  if (parts.housingLoanCredit > 0) {
    steps.push(
      {
        label: '住宅ローン控除（住民税分）',
        value: parts.housingLoanCredit,
        formula: `所得税で控除しきれなかった額（控除限度額 ${housingLoan.residentCreditLimit.toLocaleString()} 円）`,
      },
      {
        label: '所得割（住宅ローン控除後）',
        value: parts.incomeLevyAfterHousingLoan,
        formula: '所得割 - 住宅ローン控除（住民税分）',
      },
    );
  }
  steps.push(
    { label: '均等割', value: parts.equalLevy, formula: parts.equalLevy > 0 ? '固定額' : '非課税' },
    {
      label: '住民税合計',
      value: residentTax,
      formula: parts.nonTaxable ? '非課税判定により 0' : '所得割 + 均等割',
    },
  );
  return { title: '住民税の計算', steps };
}

function createNetIncomeDetail(
  annualIncome: number,
  incomeTaxWithSurtax: number,
  residentTax: number,
  socialInsurance: number,
  netAnnualIncome: number,
): CalculationDetail {
  const steps: CalculationStep[] = [
    { label: '推定年収', value: annualIncome, formula: '' },
    { label: '所得税（復興特別所得税等を含む）', value: incomeTaxWithSurtax, formula: '(控除)' },
    { label: '住民税', value: residentTax, formula: '(控除)' },
    { label: '社会保険料', value: socialInsurance, formula: '(控除)' },
    { label: '手取り年収', value: netAnnualIncome, formula: '年収 - 税金 - 社会保険料' },
  ];
  return { title: '手取り年収の計算', steps };
}

function createFurusatoNozeiDetail(
  annualIncome: number,
  socialInsurance: number,
  definedContribution: number,
  furusatoSafe: FurusatoSafeLimitResult,
  housingLoan: HousingLoanResult,
  familyData: FamilyData,
  rules: TaxRules,
): CalculationDetail {
  const incomeTaxableIncome = calculateTaxableIncome(
    annualIncome,
    socialInsurance,
    definedContribution,
    familyData,
    rules,
  );
  const incomeTaxRate = incomeTaxableIncome > 0 ? rules.incomeTaxRate(incomeTaxableIncome) : 0;
  const residentTaxableIncome = calculateResidentTaxableIncome(
    annualIncome,
    socialInsurance,
    definedContribution,
    familyData,
    rules,
  );
  const ratePercent = Math.round(rules.residentTaxRate() * 100);
  const residentTaxIncomeLevyAmount =
    residentTaxableIncome > 0 ? Math.floor((residentTaxableIncome * ratePercent) / 100) : 0;
  const specialDeduction = calculateSpecialDeduction(residentTaxIncomeLevyAmount, incomeTaxRate, rules);
  const residentParts = calculateResidentTaxParts(
    annualIncome,
    socialInsurance,
    definedContribution,
    familyData,
    rules,
    housingLoan.residentTaxCredit,
  );
  const availableLevy = residentParts.incomeLevyAfterHousingLoan;
  const filingLabel =
    familyData.furusatoFilingMethod === 'ONE_STOP' ? 'ワンストップ特例' : '確定申告';

  const steps: CalculationStep[] = [
    { label: '所得税課税所得', value: incomeTaxableIncome, formula: '年収 - 各種控除' },
    { label: '所得税率', value: Math.round(incomeTaxRate * 100), formula: '課税所得に応じた税率（%）' },
    { label: '住民税課税所得', value: residentTaxableIncome, formula: '年収 - 各種控除' },
    { label: '住民税所得割', value: residentTaxIncomeLevyAmount, formula: '課税所得 × 10%' },
    { label: '特例控除分', value: specialDeduction, formula: '住民税所得割 × 20% ÷ (90% - 所得税率×1.021)' },
    { label: '自己負担額', value: 2000, formula: '固定' },
    {
      label: '寄附金税額控除の受け皿（所得割）',
      value: availableLevy,
      formula:
        availableLevy > 0
          ? '所得割（調整控除後） - 住宅ローン控除（住民税分）'
          : '所得割が非課税、または住宅ローン控除で所得割を使い切っているため寄附金税額控除の余地がありません',
    },
    {
      label: 'ふるさと納税上限額（制度上）',
      value: furusatoSafe.institutionalLimit,
      formula: '特例控除分 + 自己負担額（住宅ローン控除を考慮しない）',
    },
  ];
  if (housingLoan.applicable) {
    steps.push(
      {
        label: '住宅ローン控除（住民税分）',
        value: housingLoan.residentTaxCredit,
        formula: `所得割（調整控除後）から先に控除される額（限度額 ${housingLoan.residentCreditLimit.toLocaleString()} 円）`,
      },
      {
        label: 'ふるさと納税上限額（損しない上限）',
        value: furusatoSafe.safeLimit,
        formula: `${filingLabel}ベース。住宅ローン控除後の所得割に寄附金税額控除（基本分+特例分）が収まる範囲`,
      },
      {
        label: '制度上の上限との差額',
        value: furusatoSafe.difference,
        formula: '制度上の上限 - 損しない上限（差額を寄附すると自己負担が増える可能性）',
      },
      {
        label: '制度上の上限まで寄附した場合の自己負担超過額',
        value: furusatoSafe.lossAtInstitutionalLimit,
        formula: '自己負担 2,000 円を超えて控除されない額（損する額）',
      },
    );
  } else {
    steps.push({
      label: 'ふるさと納税上限額（損しない上限）',
      value: furusatoSafe.safeLimit,
      formula: `${filingLabel}ベース。住宅ローン控除がないため制度上の上限と一致（所得税・住民税の競合なし）`,
    });
  }
  return { title: 'ふるさと納税控除上限額の計算', steps };
}

function createAnnualEstimatedIncomeDetail(annualIncome: number): CalculationDetail {
  return {
    title: '推定年収の詳細',
    steps: [{ label: '年間総支給額', value: annualIncome, formula: 'PDF解析および入力データから算出' }],
  };
}

function createAnnualSocialInsuranceDetail(
  totalSocialInsurance: number,
  annualHealthInsurance: number,
  annualEmploymentInsurance: number,
  annualNationalPension: number,
): CalculationDetail {
  return {
    title: '社会保険料の内訳',
    steps: [
      { label: '健康保険料（年額）', value: annualHealthInsurance, formula: '年間保険料' },
      { label: '厚生年金保険料（年額）', value: annualNationalPension, formula: '年間保険料' },
      { label: '雇用保険料（年額）', value: annualEmploymentInsurance, formula: '年間保険料' },
      { label: '社会保険料合計', value: totalSocialInsurance, formula: '各保険料の年額合計' },
    ],
  };
}

function createAnnualDefinedContributionDetail(annualDefinedContribution: number): CalculationDetail {
  return {
    title: '確定拠出年金の詳細',
    steps: [
      { label: '年額拠出額', value: annualDefinedContribution, formula: '企業型DC・iDeCoの年間拠出額' },
      { label: '所得控除', value: annualDefinedContribution, formula: '全額所得控除対象' },
    ],
  };
}

function generateCalculationDetails(
  annualIncome: number,
  healthInsurance: number,
  employmentInsurance: number,
  nationalPension: number,
  definedContribution: number,
  incomeTax: number,
  incomeTaxBeforeCredits: number,
  residentTax: number,
  netAnnualIncome: number,
  furusatoSafe: FurusatoSafeLimitResult,
  housingLoan: HousingLoanResult,
  familyData: FamilyData,
  rules: TaxRules,
): Record<string, CalculationDetail> {
  const socialInsurance = healthInsurance + employmentInsurance + nationalPension;
  const details: Record<string, CalculationDetail> = {};

  details.estimatedIncome = createAnnualEstimatedIncomeDetail(annualIncome);
  details.incomeTax = createIncomeTaxDetail(
    annualIncome,
    socialInsurance,
    definedContribution,
    incomeTax,
    incomeTaxBeforeCredits,
    housingLoan,
    familyData,
    rules,
  );
  details.residentTax = createResidentTaxDetail(
    annualIncome,
    socialInsurance,
    definedContribution,
    residentTax,
    housingLoan,
    familyData,
    rules,
  );
  details.socialInsurance = createAnnualSocialInsuranceDetail(
    socialInsurance,
    healthInsurance,
    employmentInsurance,
    nationalPension,
  );
  details.netIncome = createNetIncomeDetail(
    annualIncome,
    rules.incomeTaxWithSurtax(incomeTax),
    residentTax,
    socialInsurance,
    netAnnualIncome,
  );
  details.furusatoNozei = createFurusatoNozeiDetail(
    annualIncome,
    socialInsurance,
    definedContribution,
    furusatoSafe,
    housingLoan,
    familyData,
    rules,
  );

  if (definedContribution > 0) {
    details.definedContribution = createAnnualDefinedContributionDetail(definedContribution);
  }

  return details;
}

/**
 * 年額データから税額と明細を計算する（バックエンド calculateFromAnnualData の移植）。
 */
export function calculateTax(input: TaxCalculationInput): TaxCalculationWithDetails {
  const annualIncome = input.annualIncome;
  if (annualIncome == null || annualIncome < 0) {
    throw new Error('年間収入が無効です');
  }

  const rules = TaxRulesRegistry.of(input.taxYear ?? defaultIncomeYear());
  const familyData: FamilyData = input.familyData ?? createFamilyData();
  const healthInsurance = input.healthInsurance ?? 0;
  const employmentInsurance = input.employmentInsurance ?? 0;
  const nationalPension = input.nationalPension ?? 0;
  const definedContribution = input.definedContribution ?? 0;
  const socialInsurance = healthInsurance + employmentInsurance + nationalPension;

  const totalIncome = annualIncome - rules.salaryDeduction(annualIncome);
  const incomeTaxBeforeCredits = calculateIncomeTax(
    annualIncome,
    socialInsurance,
    definedContribution,
    familyData,
    rules,
  );

  const incomeTaxableIncome = calculateTaxableIncome(
    annualIncome,
    socialInsurance,
    definedContribution,
    familyData,
    rules,
  );
  const marginalTaxRate = incomeTaxableIncome > 0 ? rules.incomeTaxRate(incomeTaxableIncome) : 0;
  const residentTaxableIncome = calculateResidentTaxableIncome(
    annualIncome,
    socialInsurance,
    definedContribution,
    familyData,
    rules,
  );

  // 住宅ローン控除（税額控除）は所得割の調整控除後に適用される。
  const residentPartsBeforeCredits = calculateResidentTaxParts(
    annualIncome,
    socialInsurance,
    definedContribution,
    familyData,
    rules,
  );
  const housingLoan = calculateHousingLoan(familyData, {
    incomeTax: incomeTaxBeforeCredits,
    incomeTaxableIncome,
    incomeTaxBasicDeduction: rules.basicDeduction(totalIncome),
    totalIncome,
    residentIncomeLevy: residentPartsBeforeCredits.incomeLevy,
    rules,
  });

  const incomeTax = Math.max(0, incomeTaxBeforeCredits - housingLoan.incomeTaxCredit);
  const incomeTaxWithSurtax = rules.incomeTaxWithSurtax(incomeTax);
  const residentParts = calculateResidentTaxParts(
    annualIncome,
    socialInsurance,
    definedContribution,
    familyData,
    rules,
    housingLoan.residentTaxCredit,
  );
  const residentTax = residentParts.total;

  const furusatoLimit = calculateFurusatoLimit(residentTaxableIncome, marginalTaxRate, rules);
  const furusatoSafe = calculateFurusatoSafeLimit({
    institutionalLimit: furusatoLimit,
    incomeTax: incomeTaxBeforeCredits,
    marginalTaxRate,
    residentIncomeLevy: residentPartsBeforeCredits.incomeLevy,
    housingLoanCredit: housingLoan.availableCredit,
    housingLoanResidentLimit: housingLoan.residentCreditLimit,
    filingMethod: familyData.furusatoFilingMethod,
    rules,
  });

  const netAnnualIncome = annualIncome - incomeTaxWithSurtax - residentTax - socialInsurance;

  const calculationDetails = generateCalculationDetails(
    annualIncome,
    healthInsurance,
    employmentInsurance,
    nationalPension,
    definedContribution,
    incomeTax,
    incomeTaxBeforeCredits,
    residentTax,
    netAnnualIncome,
    furusatoSafe,
    housingLoan,
    familyData,
    rules,
  );

  return {
    annualIncome,
    socialInsurance,
    incomeTax,
    incomeTaxWithSurtax,
    residentTax,
    furusatoLimit,
    netAnnualIncome,
    definedContribution,
    healthInsurance,
    employmentInsurance,
    nationalPension,
    taxYear: rules.incomeYear(),
    residentFiscalYear: rules.residentFiscalYear(),
    housingLoanApplicable: housingLoan.applicable,
    housingLoanNote: housingLoan.note,
    housingLoanCreditAvailable: housingLoan.availableCredit,
    housingLoanCreditIncome: housingLoan.incomeTaxCredit,
    housingLoanCreditResident: housingLoan.residentTaxCredit,
    furusatoInstitutionalLimit: furusatoLimit,
    furusatoSafeLimit: furusatoSafe.safeLimit,
    furusatoSafeDifference: furusatoSafe.difference,
    furusatoFilingMethod: familyData.furusatoFilingMethod,
    calculationDetails,
  };
}
